/**
 * Visual Effects for Constellation
 * 
 * Visual Polish Pack v1:
 * - Nebula drift (slow, subtle animated gradient)
 * - Film grain overlay (subtle texture)
 * - Parallax depth (background stars move slower)
 * - Star twinkling animation
 * - Line shimmer/energy animation
 * - Node breathing effect
 */

/**
 * Generate deterministic "random" value based on seed
 */
function seededRandom(seed) {
  const x = Math.sin(seed * 12.9898 + seed * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * Draw nebula background with slow drift animation
 * Different per theme: Classic=none, Living=organic, Neon=sharp, Living×Neon=premium
 */
export function drawNebula(ctx, width, height, time, theme) {
  if (!theme?.bg?.nebula) return;
  
  const driftSpeed = theme.motion?.drift || 0.1;
  const t = time * 0.0001 * driftSpeed; // Very slow drift
  
  // Create gradient nebula
  const themeId = theme.id || 'living';
  
  // Theme-specific nebula colors
  const nebulaConfigs = {
    living: [
      { x: 0.3, y: 0.4, r: 0.5, color: 'rgba(160, 107, 255, 0.08)' },  // Purple
      { x: 0.7, y: 0.6, r: 0.4, color: 'rgba(107, 160, 255, 0.06)' },  // Blue
      { x: 0.5, y: 0.8, r: 0.35, color: 'rgba(255, 107, 160, 0.05)' }, // Pink
    ],
    living_neon: [
      { x: 0.25, y: 0.35, r: 0.55, color: 'rgba(180, 100, 255, 0.12)' }, // Bright purple
      { x: 0.75, y: 0.55, r: 0.45, color: 'rgba(100, 200, 255, 0.10)' }, // Cyan
      { x: 0.5, y: 0.75, r: 0.4, color: 'rgba(255, 100, 180, 0.08)' },  // Magenta
      { x: 0.6, y: 0.25, r: 0.3, color: 'rgba(255, 200, 100, 0.06)' },  // Gold accent
    ],
    neon: [
      { x: 0.4, y: 0.5, r: 0.35, color: 'rgba(0, 255, 200, 0.06)' },   // Cyan glow
      { x: 0.6, y: 0.4, r: 0.3, color: 'rgba(255, 0, 200, 0.05)' },    // Magenta
    ],
    classic: [] // No nebula for classic
  };
  
  const clouds = nebulaConfigs[themeId] || nebulaConfigs.living;
  
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  
  clouds.forEach((cloud, i) => {
    // Apply drift animation
    const driftX = Math.sin(t + i * 1.5) * 0.03;
    const driftY = Math.cos(t * 0.7 + i * 2.1) * 0.02;
    
    const cx = (cloud.x + driftX) * width;
    const cy = (cloud.y + driftY) * height;
    const r = cloud.r * Math.max(width, height);
    
    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    gradient.addColorStop(0, cloud.color);
    gradient.addColorStop(0.5, cloud.color.replace(/[\d.]+\)$/, '0.02)'));
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  });
  
  ctx.restore();
}

/**
 * Cached noise tile for grain effect (performance optimization)
 * Generated once and reused
 */
let cachedGrainTile = null;
let cachedGrainSize = 0;

function getGrainTile(size = 128) {
  if (cachedGrainTile && cachedGrainSize === size) {
    return cachedGrainTile;
  }
  
  // Create offscreen canvas for noise tile
  const offscreen = document.createElement('canvas');
  offscreen.width = size;
  offscreen.height = size;
  const ctx = offscreen.getContext('2d');
  
  // Generate noise pattern
  const imageData = ctx.createImageData(size, size);
  for (let i = 0; i < imageData.data.length; i += 4) {
    const noise = Math.random() * 60 + 20;
    imageData.data[i] = noise;     // R
    imageData.data[i + 1] = noise; // G
    imageData.data[i + 2] = noise; // B
    imageData.data[i + 3] = 255;   // A
  }
  ctx.putImageData(imageData, 0, 0);
  
  cachedGrainTile = offscreen;
  cachedGrainSize = size;
  return offscreen;
}

/**
 * Draw film grain overlay for texture (OPTIMIZED)
 * Uses cached noise tile instead of per-pixel calculation
 */
export function drawGrain(ctx, width, height, time, theme) {
  const grainIntensity = theme?.bg?.grain || 0;
  if (grainIntensity <= 0) return;
  
  const tile = getGrainTile(128);
  
  ctx.save();
  ctx.globalAlpha = grainIntensity * 0.4;
  ctx.globalCompositeOperation = 'overlay';
  
  // Tile the noise pattern across canvas with slight offset animation
  const offsetX = (time * 0.01) % 128;
  const offsetY = (time * 0.007) % 128;
  
  for (let x = -128; x < width + 128; x += 128) {
    for (let y = -128; y < height + 128; y += 128) {
      ctx.drawImage(tile, x + offsetX, y + offsetY);
    }
  }
  
  ctx.restore();
}

/**
 * Draw background stars with parallax depth
 * Background layer moves slower than foreground during pan
 */
export function drawBackgroundStarsWithParallax(
  ctx, 
  width, 
  height, 
  offset, 
  time,
  theme
) {
  const density = theme?.bg?.starfieldDensity || 80;
  const twinkleIntensity = theme?.motion?.twinkle || 0.15;
  
  ctx.save();
  
  // Create 3 depth layers
  const layers = [
    { count: Math.floor(density * 0.6), parallax: 0.1, sizeRange: [0.3, 0.6], alphaRange: [0.1, 0.2] },  // Far (slowest)
    { count: Math.floor(density * 0.3), parallax: 0.3, sizeRange: [0.5, 1.0], alphaRange: [0.15, 0.3] }, // Mid
    { count: Math.floor(density * 0.1), parallax: 0.5, sizeRange: [0.8, 1.5], alphaRange: [0.2, 0.4] },  // Near (fastest)
  ];
  
  layers.forEach((layer, layerIdx) => {
    // Apply parallax offset
    const layerOffsetX = offset.x * layer.parallax;
    const layerOffsetY = offset.y * layer.parallax;
    
    for (let i = 0; i < layer.count; i++) {
      // Deterministic position using golden angle
      const seed = layerIdx * 1000 + i;
      let x = (seededRandom(seed * 137.5) * width + layerOffsetX) % width;
      let y = (seededRandom(seed * 73.3) * height + layerOffsetY) % height;
      
      // Wrap around screen edges
      if (x < 0) x += width;
      if (y < 0) y += height;
      
      // Size and alpha
      const size = layer.sizeRange[0] + seededRandom(seed * 42.7) * (layer.sizeRange[1] - layer.sizeRange[0]);
      let alpha = layer.alphaRange[0] + seededRandom(seed * 91.3) * (layer.alphaRange[1] - layer.alphaRange[0]);
      
      // Twinkle animation
      if (twinkleIntensity > 0) {
        const twinklePhase = seededRandom(seed * 123.4) * Math.PI * 2;
        const twinkleSpeed = 0.001 + seededRandom(seed * 567.8) * 0.002;
        const twinkle = Math.sin(time * twinkleSpeed + twinklePhase) * 0.5 + 0.5;
        alpha *= 0.7 + twinkle * 0.3 * twinkleIntensity;
      }
      
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }
  });
  
  ctx.restore();
}

/**
 * Calculate node breathing effect (subtle size pulsing)
 */
export function getNodeBreathing(time, nodeIndex, theme) {
  const pulseIntensity = theme?.motion?.pulse || 0.15;
  if (pulseIntensity <= 0) return 1.0;
  
  // Each node has slightly different phase
  const phase = nodeIndex * 0.7;
  const speed = 0.0015;
  const breathe = Math.sin(time * speed + phase) * 0.5 + 0.5;
  
  // Scale: 0.97 to 1.03 (subtle)
  return 0.97 + breathe * 0.06 * pulseIntensity;
}

/**
 * Calculate line energy/shimmer effect
 */
export function getLineEnergy(time, edgeIndex, theme) {
  const energyIntensity = theme?.lines?.energy || 0;
  if (energyIntensity <= 0) return { alpha: 1.0, width: 1.0 };
  
  // Phase offset per edge
  const phase = edgeIndex * 1.3;
  const speed = 0.003;
  const wave = Math.sin(time * speed + phase) * 0.5 + 0.5;
  
  // Subtle pulse on alpha and width
  return {
    alpha: 0.85 + wave * 0.15 * energyIntensity,
    width: 0.95 + wave * 0.1 * energyIntensity
  };
}

/**
 * Calculate star shimmer effect (glow pulsing)
 */
export function getStarShimmer(time, nodeIndex, theme) {
  const shimmerIntensity = theme?.fx?.shimmer || 0;
  if (shimmerIntensity <= 0) return 1.0;
  
  const phase = nodeIndex * 2.1;
  const speed = 0.002;
  const shimmer = Math.sin(time * speed + phase) * 0.5 + 0.5;
  
  // Glow multiplier: 0.8 to 1.2
  return 0.8 + shimmer * 0.4 * shimmerIntensity;
}
